import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Bell, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function RuleChangeManagement() {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    category: "General",
    severity: "medium",
    content: "",
    summary: "",
    effectiveDate: "",
  });

  const { data: ruleChanges } = useQuery({
    queryKey: ["rule-changes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("rule_changes")
        .select("*")
        .order("published_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (ruleChange: any) => {
      const { data, error } = await supabase
        .from("rule_changes")
        .insert(ruleChange)
        .select()
        .single();
      if (error) throw error;
      
      // Broadcast to users
      await supabase.functions.invoke("broadcast-rule-change", {
        body: { ruleChangeId: data.id },
      });
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rule-changes"] });
      toast.success("Rule change published and broadcasted");
      setIsDialogOpen(false);
      setFormData({
        title: "",
        category: "General",
        severity: "medium",
        content: "",
        summary: "",
        effectiveDate: "",
      });
    },
  });

  const handlePublish = () => {
    publishMutation.mutate(formData);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Rule Change Management</h1>
            <p className="text-muted-foreground">Publish CSC compliance updates</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                New Rule Change
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Publish Rule Change</DialogTitle>
                <DialogDescription>Create and broadcast a new CSC compliance update</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="VBP">VBP</SelectItem>
                        <SelectItem value="FMV">FMV</SelectItem>
                        <SelectItem value="Eligibility">Eligibility</SelectItem>
                        <SelectItem value="Enforcement">Enforcement</SelectItem>
                        <SelectItem value="General">General</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Severity</Label>
                    <Select value={formData.severity} onValueChange={(value) => setFormData({ ...formData, severity: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Summary</Label>
                  <Input
                    value={formData.summary}
                    onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                    placeholder="Brief summary for notifications"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Full Content *</Label>
                  <Textarea
                    rows={6}
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Effective Date</Label>
                  <Input
                    type="date"
                    value={formData.effectiveDate}
                    onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
                  />
                </div>
                <Button onClick={handlePublish} className="w-full">
                  <Bell className="mr-2 h-4 w-4" />
                  Publish & Broadcast
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Published Rule Changes</CardTitle>
            <CardDescription>Recent CSC compliance updates</CardDescription>
          </CardHeader>
          <CardContent>
            {ruleChanges && ruleChanges.length > 0 ? (
              <div className="space-y-3">
                {ruleChanges.map((rule: any) => (
                  <div key={rule.id} className="p-4 border rounded-lg">
                    <div className="font-semibold">{rule.title}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {rule.category} • {rule.severity} • {new Date(rule.published_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">No rule changes published yet</div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}